import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";
import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  describeRosterWarning,
  DpRoster,
  DpRosterDay,
  minutesToTime,
  openRosterApi,
  publishRosterApi,
  saveRosterVolumesApi,
  ROSTER_KIND_LABELS,
  DpScheduleType,
  SCHEDULE_TYPE_LABELS,
  SCHEDULE_TYPE_SHORT,
  SHIFT_LABELS,
  StoreShiftCode,
  suggestRosterApi,
  unpublishRosterApi,
  updateRosterDaysApi,
  WEEKDAY_SHORT,
} from "../api/scheduleApi";
import { DP_EMPLOYEE_TAB_PAGE, DP_PAGE, DP_ROSTER_CHANGES_PAGE, DP_SETTINGS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import RosterVolumesModal from "../components/RosterVolumesModal";
import RosterPrintSheet from "../components/RosterPrintSheet";
import "./Dp.css";
import "./DpEco.css";
import "./Roster.css";

const SHORT: Record<string, string> = {
  MANHA: "M",
  NOITE: "N",
  FOLGA: "F",
  FERIADO: "Fe",
  FERIAS: "Fér",
  AFASTAMENTO: "Af",
  LOJA_FECHADA: "—",
};

/** Mês seguinte ao atual (a escala costuma ser feita antes do mês começar). */
const defaultMonth = () => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
};

const daysOf = (year: number, month: number) => {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => new Date(Date.UTC(year, month - 1, i + 1)));
};
const isoOf = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Hora x homem (ADR 0021) SUSPENSO em 03/10/2026 por decisão do usuário: a escala usa só o quadro
 * da loja e as folgas do 6x1/5x2. O botão "Informar volumes", o aviso de volumes e o
 * "escalados/necessários" ficam desligados; ligar junto com o backend (rosters.service.ts).
 */
const HORA_HOMEM_ATIVO = false;

type Editing = { employeeId: string; name: string; date: string; day?: DpRosterDay };

/**
 * Escala mensal da loja: grade colaboradores × dias. O sistema sugere (respeitando
 * folga fixa, turno, férias, afastamentos, feriados e as regras de descanso), o DP
 * ajusta clicando na célula e publica. Só a publicada aparece no portal e vale na folha.
 */
export default function RostersPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [searchParams, setSearchParams] = useSearchParams();

  const [stores, setStores] = useState<StorePublic[]>([]);
  const storeId = searchParams.get("loja") ?? "";
  const monthValue = searchParams.get("mes") ?? defaultMonth();
  const [roster, setRoster] = useState<DpRoster | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirming, setConfirming] = useState<"suggest" | "publish" | null>(null);
  const [suggestType, setSuggestType] = useState<DpScheduleType>("SEIS_POR_UM");
  const [volumesOpen, setVolumesOpen] = useState(false);

  const [year, month] = monthValue.split("-").map(Number);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    getStoresPaginatedApi(token, { all: true })
      .then((res) => {
        const items = res.items ?? [];
        setStores(items);
        if (!searchParams.get("loja") && items.length === 1) setParam("loja", items[0].id);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar as lojas."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = useCallback(async () => {
    if (!token || !storeId || !year || !month) return;
    setLoading(true);
    try {
      setRoster(await openRosterApi(token, storeId, year, month));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao abrir a escala.");
      setRoster(null);
    } finally {
      setLoading(false);
    }
  }, [token, storeId, year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<DpRoster>, success: string) => {
    setBusy(true);
    try {
      setRoster(await action());
      toast.success(success);
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível concluir.");
    } finally {
      setBusy(false);
    }
  };

  const days = useMemo(() => (year && month ? daysOf(year, month) : []), [year, month]);
  const cellMap = useMemo(() => new Map((roster?.days ?? []).map((d) => [`${d.employeeId}|${d.date}`, d])), [roster]);
  const holidays = useMemo(() => new Set(roster?.holidays ?? []), [roster]);
  const activeShifts = (roster?.shifts ?? []).filter((s) => s.isActive);

  const demandMap = useMemo(() => new Map((roster?.demand ?? []).map((d) => [d.date, d])), [roster]);
  /** Pessoas necessárias no turno do dia (hora x homem); 0 sem volume. */
  const needOf = (date: string, code: StoreShiftCode) => demandMap.get(date)?.[code] ?? 0;
  const volumeOf = useMemo(() => new Map((roster?.volumes ?? []).map((v) => [v.date, v.volume])), [roster]);

  const coverage = (date: string, code: StoreShiftCode) =>
    (roster?.days ?? []).filter((d) => d.date === date && d.kind === "TRABALHO" && d.shift === code).length;
  /** Loja aberta no dia: abre nesse dia da semana e, se for feriado, abre em feriado. */
  const isOpen = (d: Date) => {
    const hours = roster?.openingHours.find((h) => h.weekday === d.getUTCDay());
    return !!hours?.isOpen && (!holidays.has(isoOf(d)) || !!roster?.opensOnHolidays);
  };

  const readOnly = !roster || roster.payrollClosed || busy;

  const applyCell = async (patch: { kind?: "TRABALHO" | "FOLGA"; shift?: StoreShiftCode; release?: boolean }) => {
    if (!roster || !editing) return;
    const target = editing;
    setEditing(null);
    await run(() => updateRosterDaysApi(token!, roster.id, [{ employeeId: target.employeeId, date: target.date, ...patch }]), "Escala atualizada.");
  };

  /** Abre o modal do tipo de escala (6x1 ou 5x2), já marcado com o da última sugestão. */
  const openSuggest = () => {
    if (!roster) return;
    setSuggestType(roster.scheduleType);
    setConfirming("suggest");
  };
  const suggest = () => {
    if (!roster) return;
    setConfirming(null);
    run(() => suggestRosterApi(token!, roster.id, suggestType), `Escala ${SCHEDULE_TYPE_SHORT[suggestType]} sugerida. Revise e publique.`);
  };

  const ruleWarnings = (roster?.warnings ?? []).filter((w) => w.code !== "COBERTURA_ABAIXO_DO_MINIMO").length;
  const publish = (confirmed = false) => {
    if (!roster) return;
    if (!confirmed) return setConfirming("publish");
    setConfirming(null);
    run(() => publishRosterApi(token!, roster.id), "Escala publicada.");
  };

  if (!token) return null;

  const monthLabel = year && month ? new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }) : "";

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Escala da loja" }]}
            title="Escala da loja"
            subtitle="Folgas e turnos do mês por loja. O sistema sugere a partir do horário da loja, dos feriados, das férias e das preferências de cada um; você ajusta e publica."
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <div className="rosterToolbar">
            <div className="dpFormGroup">
              <label htmlFor="rosterStore">Loja</label>
              <select id="rosterStore" className="dpSelect" value={storeId} onChange={(e) => setParam("loja", e.target.value)}>
                <option value="">Escolha a loja</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeFantasia}
                  </option>
                ))}
              </select>
            </div>
            <div className="dpFormGroup">
              <label htmlFor="rosterMonth">Mês</label>
              <input id="rosterMonth" type="month" className="dpInput" value={monthValue} onChange={(e) => e.target.value && setParam("mes", e.target.value)} />
            </div>
            {roster ? (
              <div className="rosterToolbar__actions">
                <button className="dpBtn dpBtn--secondary" onClick={() => navigate(DP_ROSTER_CHANGES_PAGE())}>
                  Pedidos de troca{roster.pendingChangeRequests ? ` (${roster.pendingChangeRequests})` : ""}
                </button>
                <button className="dpBtn dpBtn--secondary" onClick={() => window.print()} disabled={!roster.days.length}>
                  Imprimir
                </button>
                {HORA_HOMEM_ATIVO ? (
                  <button className="dpBtn dpBtn--secondary" onClick={() => setVolumesOpen(true)} disabled={readOnly}>
                    Informar volumes
                  </button>
                ) : null}
                <button className="dpBtn dpBtn--secondary" onClick={openSuggest} disabled={readOnly}>
                  {roster.days.length ? "Sugerir de novo" : "Sugerir escala"}
                </button>
                {roster.status === "PUBLICADO" ? (
                  <button className="dpBtn dpBtn--secondary" onClick={() => run(() => unpublishRosterApi(token, roster.id), "Escala voltou para rascunho.")} disabled={readOnly}>
                    Voltar para rascunho
                  </button>
                ) : (
                  <button className="dpBtn dpBtn--primary" onClick={() => publish()} disabled={readOnly || !roster.days.length}>
                    Publicar
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {!storeId ? <div className="dpEmpty">Escolha a loja e o mês.</div> : null}
          {loading ? <div className="dpEmpty">Carregando...</div> : null}

          {roster && !loading ? (
            <>
              <p className={`dpNotice ${roster.status === "PUBLICADO" ? "" : "dpNotice--warning"}`}>
                <strong>{roster.storeName}</strong> — {monthLabel}:{" "}
                {roster.status === "PUBLICADO"
                  ? `publicada em ${new Date(roster.publishedAt!).toLocaleString("pt-BR")}. Os colaboradores já veem e a folha usa os dias dela; alterações valem na hora.`
                  : "rascunho. Ninguém vê até você publicar."}
                {roster.days.length ? ` Escala ${SCHEDULE_TYPE_SHORT[roster.scheduleType]}.` : ""}
                {roster.payrollClosed ? " A folha deste mês está fechada: a escala só pode ser vista." : ""}
              </p>
              {!HORA_HOMEM_ATIVO ? null : !roster.volumes.length ? (
                <p className="dpNotice dpNotice--warning rosterNoPrint">
                  Volumes do mês não informados: a sugestão só cuida das regras de descanso, sem saber quantas pessoas cada turno precisa.{" "}
                  <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setVolumesOpen(true)} disabled={readOnly}>
                    Informar volumes
                  </button>
                </p>
              ) : (
                <p className="dpNotice rosterNoPrint">
                  Hora x homem: {roster.productivity} pedidos por hora por pessoa. Volume previsto no mês:{" "}
                  {roster.volumes.reduce((sum, v) => sum + v.volume, 0).toLocaleString("pt-BR")} pedidos. Depois de mudar os volumes,
                  clique em "Sugerir de novo" para redistribuir.
                </p>
              )}
              {!roster.storeConfigured ? (
                <p className="dpNotice dpNotice--warning rosterNoPrint">
                  Loja com horário padrão.{" "}
                  <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => navigate(`${DP_SETTINGS_PAGE()}?aba=lojas`)}>
                    Configurar horário e turnos
                  </button>
                </p>
              ) : null}

              {roster.employees.length === 0 ? (
                <div className="dpEmpty">
                  Ninguém lotado nesta loja no mês. Lote os colaboradores em Cadastros → Lojas e turnos.
                </div>
              ) : (
                <>
                  <div className="rosterLegend">
                    <span><i className="rosterSwatch rosterCell--MANHA" /> M = manhã</span>
                    <span><i className="rosterSwatch rosterCell--NOITE" /> N = noite</span>
                    <span><i className="rosterSwatch rosterCell--FOLGA" /> F = folga</span>
                    <span><i className="rosterSwatch rosterCell--FERIAS" /> Férias</span>
                    <span><i className="rosterSwatch rosterCell--AFASTAMENTO" /> Afastamento</span>
                    <span><i className="rosterSwatch rosterCell--LOJA_FECHADA" /> Fechada / feriado</span>
                    <span>● fixado à mão</span>
                  </div>

                  <div className="rosterGridWrapper">
                    <table className="rosterGrid">
                      <thead>
                        <tr>
                          <th className="rosterGrid__name">Colaborador</th>
                          {days.map((d) => {
                            const key = isoOf(d);
                            const cls = holidays.has(key) ? "is-holiday" : d.getUTCDay() === 0 || d.getUTCDay() === 6 ? "is-weekend" : "";
                            return (
                              <th key={key} className={cls} title={holidays.has(key) ? "Feriado" : undefined}>
                                {d.getUTCDate()}
                                <br />
                                {WEEKDAY_SHORT[d.getUTCDay()].slice(0, 1)}
                              </th>
                            );
                          })}
                          <th>Folgas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roster.employees.map((e) => {
                          const rests = days.filter((d) => {
                            const c = cellMap.get(`${e.employeeId}|${isoOf(d)}`);
                            return c && c.kind !== "TRABALHO";
                          }).length;
                          return (
                            <tr key={e.employeeId}>
                              <td className="rosterGrid__name" title={e.name}>
                                <button
                                  type="button"
                                  className="rosterCell"
                                  style={{ textAlign: "left", fontWeight: 500 }}
                                  onClick={() => navigate(DP_EMPLOYEE_TAB_PAGE(e.employeeId, "escala"))}
                                >
                                  {e.name}
                                  <small>
                                    {e.shift === "ROTATIVO" ? "Rotativo" : SHIFT_LABELS[e.shift as StoreShiftCode]}
                                    {e.fixedDayOff !== null ? ` · folga ${WEEKDAY_SHORT[e.fixedDayOff].toLowerCase()}` : ""}
                                  </small>
                                </button>
                              </td>
                              {days.map((d) => {
                                const date = isoOf(d);
                                const c = cellMap.get(`${e.employeeId}|${date}`);
                                if (!c) {
                                  return (
                                    <td key={date}>
                                      <span className="rosterCell rosterCell--empty" style={{ display: "block" }} title="Fora do contrato ou ainda sem sugestão" />
                                    </td>
                                  );
                                }
                                const variant = c.kind === "TRABALHO" ? c.shift ?? "MANHA" : c.kind;
                                const label = c.kind === "TRABALHO" ? SHORT[c.shift ?? ""] ?? "?" : SHORT[c.kind];
                                const title =
                                  c.kind === "TRABALHO"
                                    ? `${SHIFT_LABELS[c.shift as StoreShiftCode] ?? "Sem turno"} ${minutesToTime(c.startMinute)}–${minutesToTime(c.endMinute)}`
                                    : ROSTER_KIND_LABELS[c.kind];
                                // FERIADO também: a escala não considera mais feriado (03/10/2026), e dias gravados antes precisam ser corrigidos à mão.
                                const editable = !readOnly && (c.kind === "TRABALHO" || c.kind === "FOLGA" || c.kind === "FERIADO");
                                return (
                                  <td key={date}>
                                    <button
                                      type="button"
                                      className={`rosterCell rosterCell--${variant}${c.pinned ? " is-pinned" : ""}`}
                                      title={title + (c.pinned ? " (fixado)" : "")}
                                      disabled={!editable}
                                      onClick={() => setEditing({ employeeId: e.employeeId, name: e.name, date, day: c })}
                                    >
                                      {label}
                                    </button>
                                  </td>
                                );
                              })}
                              <td>{rests}</td>
                            </tr>
                          );
                        })}
                        {activeShifts.map((s) => (
                          <tr key={s.code} className="rosterCoverage">
                            <td className="rosterGrid__name">
                              {SHIFT_LABELS[s.code]} <small>{HORA_HOMEM_ATIVO ? "escalados / necessários" : "escalados"}</small>
                            </td>
                            {days.map((d) => {
                              const date = isoOf(d);
                              if (!isOpen(d)) {
                                return <td key={date}>—</td>;
                              }
                              const count = coverage(date, s.code);
                              const needed = needOf(date, s.code);
                              return (
                                HORA_HOMEM_ATIVO ? (
                                  <td
                                    key={date}
                                    className={count < needed ? "is-low" : "is-ok"}
                                    title={volumeOf.has(date) ? `Volume ${volumeOf.get(date)}: precisa de ${needed}` : "Sem volume informado"}
                                  >
                                    {count}/{needed}
                                  </td>
                                ) : (
                                  <td key={date}>{count}</td>
                                )
                              );
                            })}
                            <td />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {roster.days.length === 0 ? (
                    <p className="dpNotice rosterNoPrint" style={{ marginTop: 12 }}>
                      Escala vazia. Clique em "Sugerir escala": o sistema preenche o mês e você ajusta.
                    </p>
                  ) : null}

                  {roster.warnings.length ? (
                    <div className="dpNotice dpNotice--warning rosterWarnings rosterNoPrint">
                      <strong>{roster.warnings.length} aviso(s)</strong>
                      <ul>
                        {roster.warnings.map((w, i) => (
                          <li key={i}>{describeRosterWarning(w)}</li>
                        ))}
                      </ul>
                    </div>
                  ) : roster.days.length ? (
                    <p className="dpNotice rosterNoPrint" style={{ marginTop: 12 }}>
                      Sem avisos: regras de descanso respeitadas.
                    </p>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </div>
      </main>

      <EcoConfirmDialog
        open={confirming === "suggest"}
        title={roster?.days.length ? "Sugerir de novo" : "Sugerir escala"}
        message={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <span>Tipo de escala da loja neste mês (vale para todos):</span>
            {(Object.keys(SCHEDULE_TYPE_LABELS) as DpScheduleType[]).map((type) => (
              <label key={type} className="dpCheckboxRow" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  name="suggestType"
                  value={type}
                  checked={suggestType === type}
                  onChange={() => setSuggestType(type)}
                />{" "}
                {SCHEDULE_TYPE_LABELS[type]}
              </label>
            ))}
            {roster?.days.length ? (
              <span className="dpFormHint">As células que você fixou à mão (com ponto) ficam como estão; o resto do mês é refeito.</span>
            ) : null}
          </div>
        }
        confirmLabel="Sugerir"
        busy={busy}
        onConfirm={suggest}
        onCancel={() => setConfirming(null)}
      />
      <EcoConfirmDialog
        open={confirming === "publish"}
        title="Publicar a escala?"
        message={
          ruleWarnings ? (
            <>
              Há <strong>{ruleWarnings} aviso(s) de regra de descanso</strong>. Publicando, os colaboradores passam a ver a escala no
              portal e a folha passa a usar os dias dela.
            </>
          ) : (
            "Os colaboradores passam a ver a escala no portal e a folha passa a usar os dias dela."
          )
        }
        confirmLabel="Publicar"
        busy={busy}
        onConfirm={() => publish(true)}
        onCancel={() => setConfirming(null)}
      />

      {/* Folha de impressão com a identidade da empresa (só aparece ao imprimir). */}
      {roster && roster.days.length ? <RosterPrintSheet roster={roster} /> : null}

      {volumesOpen && roster ? (
        <RosterVolumesModal
          roster={roster}
          busy={busy}
          onClose={() => setVolumesOpen(false)}
          onSave={async (volumes) => {
            setBusy(true);
            try {
              setRoster(await saveRosterVolumesApi(token!, roster.id, volumes));
              setVolumesOpen(false);
              toast.success("Volumes salvos. Clique em \"Sugerir de novo\" para redistribuir a escala.");
            } catch (error: any) {
              toast.error(error?.message || "Não foi possível salvar os volumes.");
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}

      {editing ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {editing.name} — {new Date(`${editing.date}T00:00:00Z`).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit", timeZone: "UTC" })}
              </h2>
              <button className="dpModal__close" onClick={() => setEditing(null)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <p className="dpFormHint">O que você escolher fica fixado: a próxima sugestão não mexe neste dia.</p>
              <div className="rosterCellMenu">
                {activeShifts.map((s) => (
                  <button key={s.code} className="dpBtn dpBtn--secondary" onClick={() => applyCell({ kind: "TRABALHO", shift: s.code })}>
                    Trabalho — {SHIFT_LABELS[s.code]}
                  </button>
                ))}
                <button className="dpBtn dpBtn--secondary" onClick={() => applyCell({ kind: "FOLGA" })}>
                  Folga
                </button>
                {editing.day?.pinned ? (
                  <button className="dpBtn dpBtn--secondary" onClick={() => applyCell({ release: true })}>
                    Soltar (deixar para a sugestão)
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
